import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { FiArrowRight, FiArrowUpRight, FiCalendar, FiUser } from 'react-icons/fi';
import SEO from '../components/SEO';
import { useContent } from '../hooks/useContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULTS = {
  hero_welcome: 'Welcome to Arvaron Group',
  hero_title_line1: 'Driven by Ambition.',
  hero_title_line2: 'Built for the World.',
  hero_subtitle: 'Shaping Tomorrow\'s Global Economy',
  hero_description: 'ARVARON is a premier Indian holding company driving innovation and scale across strategic verticals: Logistics, Sourcing, Real Estate, Retail, and Digital Infrastructure.',
  hero_image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab',
  hero_cta_primary: 'Explore Our Verticals',
  hero_cta_secondary: 'Corporate Overview',
  stats: [
    { value: '7', label: 'Business Verticals', description: 'Diversified across key sectors' },
    { value: '15+', label: 'Global Markets', description: 'Countries reached worldwide' },
    { value: '5+', label: 'Strategic Pillars', description: 'Core business foundations' },
    { value: '100%', label: 'Committed to Growth', description: 'Unwavering dedication' }
  ],
  stats_section_label: 'Scale & Reach',
  stats_section_title: 'Built at Scale,',
  stats_section_highlight: 'Designed for More',
  stats_section_description: "ARVARON's numbers reflect disciplined growth across India's fastest-evolving sectors.",
  verticals_title: 'A Diversified Portfolio of',
  verticals_title_highlight: 'Market Leaders',
  verticals_subtitle: "Seven distinct verticals, one unified vision — building India's next generation conglomerate.",
  cta_title_line1: 'Ready to Build the',
  cta_title_highlight: 'Future Together?',
  cta_description: 'Partner with Arvaron to access world-class capabilities across strategic business verticals.'
};

const Home = () => {
  const { content: cms } = useContent('homepage', DEFAULTS);
  const [testimonials, setTestimonials] = useState([]);
  const [latestBlogs, setLatestBlogs] = useState([]);

  useEffect(() => {
    fetchTestimonials();
    fetchLatestBlogs();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/testimonials`);
      setTestimonials(response.data.slice(0, 3));
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    }
  };

  const fetchLatestBlogs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blog`);
      setLatestBlogs(response.data.slice(0, 3));
    } catch (error) {
      console.error('Failed to fetch blogs:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const verticals = [
    { title: 'Arvaron Sourcing', subtitle: 'Sourcing & Procurement', description: 'Strategic global sourcing solutions connecting markets, ensuring quality, and optimizing cost structures.', image: 'https://images.pexels.com/photos/5860937/pexels-photo-5860937.jpeg', path: '/business/sourcing' },
    { title: 'Arvaron Logistics', subtitle: 'Logistics & Supply Chain', description: 'End-to-end global freight, warehousing, and distribution networks engineered for resilience.', image: 'https://images.unsplash.com/photo-1653794280510-6d0c380449a7', path: '/business/logistics' },
    { title: 'Arvaron Exports', subtitle: 'Imports & Exports', description: 'Facilitating seamless international trade with deep EXIM expertise and trade finance solutions.', image: 'https://images.pexels.com/photos/9806482/pexels-photo-9806482.jpeg', path: '/business/exports' },
    { title: 'Arvaron Technologies', subtitle: 'IT Consulting & Software', description: 'Driving digital transformation through custom software development and cloud innovation.', image: 'https://images.unsplash.com/photo-1499951360447-b19be8fe80f5', path: '/business/technologies' },
    { title: 'Arvaron Digital', subtitle: 'Digital Marketing', description: 'Data-driven marketing technology and performance campaigns for the modern enterprise.', image: 'https://images.unsplash.com/photo-1718220216044-006f43e3a9b1', path: '/business/digital' },
    { title: 'Arvaron Infra', subtitle: 'Real Estate', description: 'Development of landmark commercial and residential properties across high-growth markets.', image: 'https://images.unsplash.com/photo-1527576539890-dfa815648363', path: '/business/infra' },
    { title: 'Arvaron Retail', subtitle: 'Retail', description: 'Curated retail experiences connecting consumers with quality products across India.', image: 'https://images.pexels.com/photos/35782401/pexels-photo-35782401.jpeg', path: '/business/retail' }
  ];

  return (
    <div className="bg-black text-white" data-testid="home-page">
      <SEO title="Home" description={cms.hero_description} url="/" />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden -mt-28">
        <div className="absolute inset-0 z-0">
          <img
            src={cms.hero_image}
            alt="Hero"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/60 to-black" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: 'easeOut' }}
          >
            <p className="text-sm uppercase tracking-[0.3em] text-white/40 mb-8" data-testid="hero-location">
              {cms.hero_welcome}
            </p>

            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold mb-8 leading-[0.95]" data-testid="hero-title">
              {cms.hero_title_line1}<br />
              <span className="brand-gradient-text">
                {cms.hero_title_line2}
              </span>
            </h1>

            <p className="text-lg md:text-xl text-white/60 max-w-3xl mx-auto mb-4 leading-relaxed" data-testid="hero-subtitle">
              {cms.hero_subtitle || 'Shaping Tomorrow\'s Global Economy'}
            </p>

            <p className="text-base text-white/40 max-w-3xl mx-auto mb-14 leading-relaxed">
              {cms.hero_description}
            </p>

            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <Link
                to="/businesses"
                data-testid="hero-explore-button"
                className="group px-10 py-4 brand-gradient-bg text-black font-semibold rounded-sm hover:opacity-90 transition-all inline-flex items-center justify-center text-sm uppercase tracking-wider"
              >
                {cms.hero_cta_primary}
                <FiArrowRight className="ml-3 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/about"
                data-testid="hero-about-button"
                className="group px-10 py-4 bg-transparent border border-white/20 text-white font-semibold rounded-sm hover:bg-white/5 hover:border-white/40 transition-all inline-flex items-center justify-center text-sm uppercase tracking-wider"
              >
                {cms.hero_cta_secondary}
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-[1px] h-12 bg-gradient-to-b from-transparent via-white/30 to-transparent" />
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-black relative" data-testid="stats-section">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#17B9BE]/20 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#17B9BE] mb-4">{cms.stats_section_label || 'Scale & Reach'}</p>
            <h2 className="font-display text-4xl md:text-6xl font-bold mb-6">
              {cms.stats_section_title || 'Built at Scale,'} <br />
              <span className="brand-gradient-text">{cms.stats_section_highlight || 'Designed for More'}</span>
            </h2>
            <p className="text-white/40 text-lg max-w-2xl mx-auto">
              {cms.stats_section_description || "ARVARON's numbers reflect disciplined growth across India's fastest-evolving sectors."}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
            {(cms.stats || []).map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="text-center"
                data-testid={`stat-${index}`}
              >
                <div className="text-5xl md:text-6xl font-bold brand-gradient-text mb-3">{stat.value}</div>
                <div className="text-sm uppercase tracking-[0.15em] text-white/70 mb-1 font-medium">{stat.label}</div>
                <div className="text-xs text-white/30">{stat.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Corporate Overview */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="corporate-overview-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1764534161906-f08540a2d333?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbW9vZHklMjBza3lzY3JhcGVyJTIwYnVpbGRpbmclMjBjb3Jwb3JhdGUlMjBuaWdodCUyMGJsdWV8ZW58MHx8fHwxNzc2MjAwODIwfDA&ixlib=rb-4.1.0&q=85"
                alt="Corporate cityscape"
                className="rounded w-full h-full object-cover"
              />
              <div className="absolute inset-0 rounded border border-white/5" />
              {/* Floating badges */}
              <div className="absolute top-6 left-6 bg-black/70 backdrop-blur-md px-4 py-2 rounded border border-white/10">
                <p className="text-[10px] uppercase tracking-widest text-white/40">Headquarters</p>
                <p className="text-sm font-semibold">Mumbai, India</p>
              </div>
              <div className="absolute bottom-6 right-6 bg-black/70 backdrop-blur-md px-4 py-2 rounded border border-white/10">
                <p className="text-[10px] uppercase tracking-widest text-white/40">Founded</p>
                <p className="text-sm font-semibold">2025</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <p className="text-xs uppercase tracking-[0.3em] text-[#17B9BE] mb-4">Corporate Overview</p>
              <h2 className="font-display text-4xl md:text-5xl font-bold mb-8">
                A Legacy Built on<br />
                <span className="brand-gradient-text">Vision and Execution.</span>
              </h2>
              <div className="space-y-5 text-white/50 leading-relaxed">
                <p>
                  Founded in Mumbai in 2025, ARVARON emerged with a clear mandate: to consolidate and scale high-potential businesses across foundational sectors of the modern economy.
                </p>
                <p>
                  We operate as a dynamic holding entity, deploying strategic capital, operational expertise, and technological integration across our portfolio. From global supply chains to premium real estate and digital infrastructure, our approach is defined by rigorous governance, sustainable practices, and an unwavering commitment to value creation.
                </p>
              </div>
              
              <div className="grid grid-cols-3 gap-8 mt-10 pt-10 border-t border-white/5">
                <div>
                  <div className="text-3xl font-bold brand-gradient-text mb-1">5+</div>
                  <div className="text-xs text-white/40 uppercase tracking-wider">Strategic Verticals</div>
                </div>
                <div>
                  <div className="text-3xl font-bold brand-gradient-text mb-1">15+</div>
                  <div className="text-xs text-white/40 uppercase tracking-wider">Global Markets</div>
                </div>
                <div>
                  <div className="text-3xl font-bold brand-gradient-text mb-1">100%</div>
                  <div className="text-xs text-white/40 uppercase tracking-wider">Committed to Growth</div>
                </div>
              </div>

              <div className="mt-10 p-6 bg-black/50 rounded border-l-2 border-[#17B9BE]">
                <p className="text-white/50 italic leading-relaxed">
                  "Our foundation in Mumbai connects us to one of the world's fastest-growing economies, while our vision remains resolutely global."
                </p>
              </div>

              <Link
                to="/about"
                className="group inline-flex items-center mt-8 text-[#17B9BE] hover:text-white transition-colors text-sm font-medium"
              >
                Learn Our Story
                <FiArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Business Verticals */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="verticals-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <p className="text-xs uppercase tracking-[0.3em] text-[#17B9BE] mb-4">Our Businesses</p>
            <h2 className="font-display text-4xl md:text-6xl font-bold mb-6">
              {cms.verticals_title} <br />
              <span className="brand-gradient-text">{cms.verticals_title_highlight}</span>
            </h2>
            <p className="text-white/40 text-lg max-w-2xl mx-auto">
              {cms.verticals_subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {verticals.map((vertical, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                viewport={{ once: true }}
                data-testid={`vertical-card-${index}`}
              >
                <Link
                  to={vertical.path}
                  className="group block bg-[#111] rounded overflow-hidden gradient-border hover:bg-[#151515] transition-all duration-300"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={vertical.image}
                      alt={vertical.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-black/40 to-transparent" />
                  </div>
                  <div className="p-6">
                    <p className="text-xs uppercase tracking-[0.15em] text-[#17B9BE] mb-2">{vertical.subtitle}</p>
                    <h3 className="text-xl font-bold mb-3">{vertical.title}</h3>
                    <p className="text-white/40 text-sm mb-5 leading-relaxed">{vertical.description}</p>
                    <div className="flex items-center text-white/60 group-hover:text-[#17B9BE] transition-colors">
                      <span className="text-xs uppercase tracking-wider font-medium">Explore</span>
                      <FiArrowUpRight className="ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-24 bg-black" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-20">
              <p className="text-xs uppercase tracking-[0.3em] text-[#17B9BE] mb-4">Testimonials</p>
              <h2 className="font-display text-4xl md:text-6xl font-bold mb-6">
                Trusted by <span className="brand-gradient-text">Industry Leaders</span>
              </h2>
              <p className="text-white/40 text-lg">
                What our partners say about working with Arvaron
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-[#0a0a0a] p-8 rounded border border-white/5 hover:border-white/10 transition-colors"
                  data-testid={`testimonial-${index}`}
                >
                  <div className="flex mb-5">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="text-[#72EA43] text-lg">★</span>
                    ))}
                  </div>
                  <p className="text-white/60 mb-8 leading-relaxed text-sm">"{testimonial.content}"</p>
                  <div className="border-t border-white/5 pt-5">
                    <p className="font-semibold text-sm">{testimonial.name}</p>
                    <p className="text-xs text-white/40 mt-1">{testimonial.position}, {testimonial.company}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Latest Blogs */}
      {latestBlogs.length > 0 && (
        <section className="py-24 bg-[#0a0a0a]" data-testid="latest-blogs-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-end justify-between mb-16">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-[#17B9BE] mb-4">From the Blog</p>
                <h2 className="font-display text-4xl md:text-5xl font-bold">
                  Latest <span className="brand-gradient-text">Insights</span>
                </h2>
              </div>
              <Link
                to="/blog"
                className="group hidden md:inline-flex items-center text-[#17B9BE] hover:text-white transition-colors text-sm font-medium"
              >
                View All Articles
                <FiArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {latestBlogs.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  data-testid={`latest-blog-${index}`}
                >
                  <Link
                    to={`/blog/${post.slug}`}
                    className="group block bg-black rounded overflow-hidden border border-white/5 hover:border-[#17B9BE]/20 transition-all duration-300 gradient-border"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={post.image_url}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                      <div className="absolute bottom-4 left-4">
                        <span className="px-3 py-1 bg-[#17B9BE]/20 backdrop-blur-sm rounded text-xs font-semibold text-[#17B9BE] border border-[#17B9BE]/20">
                          {post.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center text-[10px] text-white/30 mb-3 space-x-4 uppercase tracking-wider">
                        <span className="flex items-center">
                          <FiCalendar className="mr-1" size={10} />
                          {formatDate(post.published_at)}
                        </span>
                        <span className="flex items-center">
                          <FiUser className="mr-1" size={10} />
                          {post.author}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold mb-2 group-hover:text-[#17B9BE] transition-colors leading-snug">
                        {post.title}
                      </h3>
                      <p className="text-white/35 text-sm line-clamp-2 leading-relaxed">{post.excerpt}</p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            <div className="mt-10 text-center md:hidden">
              <Link
                to="/blog"
                className="group inline-flex items-center text-[#17B9BE] hover:text-white transition-colors text-sm font-medium"
              >
                View All Articles
                <FiArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-32 bg-[#0a0a0a] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 hero-overlay" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-4xl md:text-6xl font-bold mb-8">
              {cms.cta_title_line1}<br />
              <span className="brand-gradient-text">{cms.cta_title_highlight}</span>
            </h2>
            <p className="text-white/40 text-lg mb-12 max-w-2xl mx-auto">
              {cms.cta_description}
            </p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <Link
                to="/contact"
                data-testid="cta-contact-button"
                className="group px-10 py-4 brand-gradient-bg text-black font-semibold rounded-sm hover:opacity-90 transition-all inline-flex items-center justify-center text-sm uppercase tracking-wider"
              >
                Get in Touch
                <FiArrowRight className="ml-3 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/careers"
                data-testid="cta-careers-button"
                className="px-10 py-4 bg-transparent border border-white/20 text-white font-semibold rounded-sm hover:bg-white/5 hover:border-white/40 transition-all inline-flex items-center justify-center text-sm uppercase tracking-wider"
              >
                Join Our Team
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
